import React from 'react';
import { Alert, Button, Card, Flex, Typography, theme } from 'antd';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  LeftOutlined,
  LoadingOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';

import { CfFlowResponse, CfFlowStatus } from '../api/cloudflareFlowApi';
import { usePrerenderToken } from '../../../hooks/usePrerenderUser';
import { screenNexusPrompt } from '../nexus/screenNexusPrompt';
import { type CfNexusOpening, errorShownOf } from '../cfNexusOpening';
import { CF_SETUP_STEPS, StepDisplayState, setupRowStates } from './cfSetupSteps';
import { CfVerificationRetry, isRetryPending } from './cfVerificationRetry';
import CloudflareErrorCard from './CloudflareErrorCard';
import CloudflareFailureCard from './CloudflareFailureCard';
import CloudflareStepHeader from './CloudflareStepHeader';

const { Paragraph, Text } = Typography;

const SETUP_RUNNING: readonly CfFlowStatus[] = [
  'preflight_running',
  'deploying',
  'verifying',
];

const StepIcon = ({ state }: { state: StepDisplayState }) => {
  const { token } = theme.useToken();

  switch (state) {
    case 'done':
      return <CheckCircleFilled style={{ color: token.colorSuccess }} />;
    case 'failed':
      return <CloseCircleFilled style={{ color: token.colorError }} />;
    case 'running':
      return <LoadingOutlined style={{ color: token.colorPrimary }} />;
    default:
      return <MinusCircleOutlined style={{ color: token.colorTextQuaternary }} />;
  }
};

export type CloudflareProgressStepProps = {
  flow: CfFlowResponse;
  onGoBack?: () => void;
  onOpenNexus?: (opening: CfNexusOpening) => void;
  onRetrySetup?: () => void;
  onRetryVerification?: () => void;
  verificationRetry?: CfVerificationRetry;
};

/**
 * What the customer watches while the server works (PRE-3292 / F4).
 *
 * The screen only **shows** state. The deployment runs to completion on the
 * server, so closing the tab does not stop it — which is also why this screen
 * never drives the work, only follows it.
 */
const CloudflareProgressStep = ({
  flow,
  onGoBack,
  onOpenNexus,
  onRetrySetup,
  onRetryVerification,
  verificationRetry = 'none',
}: CloudflareProgressStepProps) => {
  const { token } = theme.useToken();
  const retryPending = isRetryPending(verificationRetry);
  const retryErrored = verificationRetry === 'errored';
  const shownStatus: CfFlowStatus =
    retryPending && flow.status === 'verification_failed' ? 'verifying' : flow.status;
  const hasFailed = flow.screen === 'error' && !retryPending;
  const setupFailed = hasFailed && flow.status !== 'verification_failed';
  const verificationFailed = hasFailed && flow.status === 'verification_failed' && !retryErrored;
  const isSetupRunning = SETUP_RUNNING.includes(shownStatus);
  const rowStates = setupRowStates(flow.steps, shownStatus);
  const prerenderToken = usePrerenderToken();

  return (
    <Flex vertical gap={token.marginLG}>
      <CloudflareStepHeader
        title="Setting up integration"
        description="We're configuring Cloudflare so crawlers see your fully rendered pages."
      />

      {setupFailed && (
        <CloudflareErrorCard flow={flow} onOpenNexus={onOpenNexus} />
      )}

      {retryPending && (
        <Alert
          type="info"
          showIcon
          message="We couldn't confirm Prerender on your domain yet"
          description={
            verificationRetry === 'running'
              ? 'Checking again now.'
              : "After a nameserver change, traffic can take a few minutes to reach Cloudflare. We'll check again in a minute."
          }
        />
      )}

      {retryErrored && (
        <Alert
          type="warning"
          showIcon
          message="We couldn't run the second check"
          description="The check did not reach Prerender. Try again when you are ready."
          action={
            <Button size="small" onClick={onRetryVerification}>
              Try again
            </Button>
          }
        />
      )}

      {verificationFailed && (
        <CloudflareFailureCard
          title="We still couldn't confirm Prerender on your domain"
          askNexusLabel="Ask Nexus how to verify"
          guidesLabel="View guides"
          onAskNexus={() =>
            onOpenNexus?.({
              fromStep: 'verification',
              errorShown: errorShownOf(flow),
              prefillText: screenNexusPrompt({
                flow,
                screen: flow.screen,
                verificationRetry,
                knownSecrets: [prerenderToken],
              }),
            })
          }
        >
          <Paragraph style={{ marginBottom: 0 }}>
            Your Cloudflare setup is in place, but our check did not see Prerender
            answering yet. Nexus can walk you through checking it and running the
            verification yourself.
          </Paragraph>
        </CloudflareFailureCard>
      )}

      <Card>
        {/* Centred as a block, so the rows stay left-aligned with each other
            rather than each one centring on its own label length. */}
        <Flex justify="center">
          {/* Announced, so somebody using a screen reader learns that a step
              finished rather than watching a silent list. */}
          <Flex vertical gap={token.marginSM} role="list" aria-live="polite">
            {CF_SETUP_STEPS.map(({ key, label }, index) => {
              const state = rowStates[index];

              return (
                <Flex key={key} align="center" gap={token.marginSM} role="listitem">
                  <StepIcon state={state} />
                  <Text
                    type={state === 'failed' ? 'danger' : undefined}
                    aria-label={`${label}: ${state}`}
                  >
                    {label}
                  </Text>
                </Flex>
              );
            })}
          </Flex>
        </Flex>
      </Card>

      {(onGoBack || ((hasFailed || retryPending) && onRetrySetup)) && (
        <Flex justify="space-between" align="center">
          {onGoBack ? (
            <Button
              type="link"
              icon={<LeftOutlined />}
              disabled={isSetupRunning}
              onClick={onGoBack}
            >
              Back
            </Button>
          ) : (
            <span />
          )}
          {(hasFailed || retryPending) && onRetrySetup && (
            <Button type="primary" disabled={retryPending} onClick={onRetrySetup}>
              Retry Setup
            </Button>
          )}
        </Flex>
      )}
    </Flex>
  );
};

export default CloudflareProgressStep;
